import type { UseToastOptions } from '@chakra-ui/react';
import { useEffect } from 'react';
import ReactDatePicker from 'react-datepicker';
import {
  Controller,
  type RegisterOptions,
  type UseFormReturn,
  useWatch,
} from 'react-hook-form';
import type { AnyVariables, OperationContext, OperationResult } from 'urql';

import {
  isRoleRevelioAdmin,
  isValidEmail,
  useGetLoggedInUser,
} from '@revelio/auth';
import { StorageKey } from '@revelio/core';
import {
  type Client,
  MetadataKey,
  Role,
  type UpdateUserMutation,
  type UpdateUserMutationVariables,
  type User,
} from '@revelio/data-access';
import { Button, FormField, Input, Select, Switch } from '@revelio/ui';

import { AdminUserClientGroupSelect } from './admin-user-edit/admin-user-client-group-select';
import './user-form.css';

import {
  generatePassword,
  getMetadataValue,
  type OptionValues,
  roleOptions,
  USER_ROLE_OPTION,
} from './utils/helpers';

export type UserFormValues = {
  active: boolean;
  trialExpirationDate: string;
  name: string;
  email: string;
  password?: string;
  role: {
    label: string;
    value: Role;
  };
  client_group: {
    label: string;
    value: string;
  };
  sourcewhale_key?: string;
  skip_share_of_postings_scaling?: boolean;
};

type Props = {
  useFormMethods: UseFormReturn<UserFormValues>;
  userToEdit?: User | null;
  setPassword: (newPassword: string) => void;
  setTrialExpirationDate?: (newExpiration: Date) => void;
  passwordRules?: RegisterOptions<UserFormValues, 'password'>;
  defaultClient?: Client;
  hideChangeClientGroup?: boolean;
};
const UserForm = ({
  useFormMethods,
  userToEdit,
  setPassword,
  setTrialExpirationDate,
  passwordRules = {
    required: 'Password is required.',
    minLength: {
      value: 4,
      message: 'Minimum length should be 4',
    },
  },
  defaultClient,
  hideChangeClientGroup,
}: Props) => {
  const { loggedInUser } = useGetLoggedInUser();
  const isRevelioAdmin = isRoleRevelioAdmin(loggedInUser.role);
  const roleSelectOptions = roleOptions(loggedInUser.role);

  const isAddingNewUser = userToEdit === null;

  const trialExpirationDate = useWatch({
    control: useFormMethods.control,
    name: 'trialExpirationDate',
  });
  const savedTrialExpirationValue = getMetadataValue(
    userToEdit?.metadata,
    MetadataKey.ExpirationDate
  );
  const savedTrialExpirationDate = savedTrialExpirationValue
    ? new Date(savedTrialExpirationValue)
    : undefined;

  const savedSourcewhaleKey = getMetadataValue(
    userToEdit?.metadata,
    MetadataKey.SourcewhaleKey
  );

  useEffect(() => {
    const storedValue = localStorage.getItem(
      StorageKey.SkipShareOfPostingsScaling
    );
    if (storedValue !== null) {
      useFormMethods.setValue(
        'skip_share_of_postings_scaling',
        storedValue === 'true'
      );
    }
  }, [useFormMethods]);

  const skipShareOfPostingsScaling = useWatch({
    control: useFormMethods.control,
    name: 'skip_share_of_postings_scaling',
  });

  useEffect(() => {
    if (skipShareOfPostingsScaling !== undefined) {
      localStorage.setItem(
        StorageKey.SkipShareOfPostingsScaling,
        skipShareOfPostingsScaling.toString()
      );
    }
  }, [skipShareOfPostingsScaling]);

  const { errors } = useFormMethods.formState;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {isRevelioAdmin && !isAddingNewUser && (
        <FormField label="Active">
          <Switch
            id="active"
            size="sm"
            inactiveLabel="Off"
            label="On"
            disabled={!isRevelioAdmin}
            defaultChecked={userToEdit?.active as boolean}
            {...useFormMethods.register('active')}
          />
        </FormField>
      )}

      {isRevelioAdmin &&
        !isAddingNewUser &&
        userToEdit?.role !== Role.RevelioAdmin && (
          <FormField label="Trial end date">
            <ReactDatePicker
              selected={
                trialExpirationDate
                  ? new Date(trialExpirationDate)
                  : savedTrialExpirationDate
              }
              onChange={(newDate) => {
                setTrialExpirationDate?.(newDate as Date);
              }}
              dateFormat="MMMM d, yyyy h:mm aa"
              showTimeSelect={true}
              yearDropdownItemNumber={15}
              scrollableYearDropdown
              showYearDropdown={false}
              dropdownMode="select"
              customInput={<Input size="sm" />}
              onBlur={(e) => {
                e.preventDefault();
              }}
            />
          </FormField>
        )}

      <FormField
        label="Name"
        errors={errors.name?.message ? [errors.name.message] : undefined}
      >
        <Input
          isInvalid={!!errors.name}
          defaultValue={userToEdit?.name || ''}
          {...useFormMethods.register('name', {
            required: 'Name is required.',
            minLength: {
              value: 1,
              message: 'Minimum length should be 1',
            },
          })}
        />
      </FormField>

      <FormField
        label="Email"
        errors={errors.email?.message ? [errors.email.message] : undefined}
      >
        <Input
          isInvalid={!!errors.email}
          defaultValue={userToEdit?.email?.trim() || ''}
          {...useFormMethods.register('email', {
            required: 'Email is required.',
            validate: {
              emailSyntax: (email: string) =>
                isValidEmail(email) || 'Valid email is required',
            },
            onChange: (e) => {
              e.target.value = e.target.value.trim();
            },
          })}
        />
      </FormField>

      <FormField
        label="Password"
        errors={
          errors.password?.message ? [errors.password.message] : undefined
        }
      >
        <Input
          isInvalid={!!errors.password}
          {...useFormMethods.register('password', passwordRules)}
          endSlot={
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                generatePassword((newPassword) => setPassword(newPassword))
              }
            >
              Generate
            </Button>
          }
        />
      </FormField>

      {isRevelioAdmin && (
        <FormField label="Role">
          <Controller
            name="role"
            control={useFormMethods.control}
            defaultValue={
              userToEdit
                ? roleSelectOptions.find((r) => r.value === userToEdit.role)
                : USER_ROLE_OPTION
            }
            render={({ field }) => (
              <Select<OptionValues>
                {...field}
                name="role"
                options={roleSelectOptions}
                placeholder="Select role..."
                closeMenuOnSelect={false}
                noOptionsMessage={() => null}
                defaultValue={roleSelectOptions.filter(
                  (i) => i.default === true
                )}
                isClearable={false}
              />
            )}
          />
        </FormField>
      )}

      {isRevelioAdmin && !hideChangeClientGroup && (
        <AdminUserClientGroupSelect
          control={useFormMethods.control}
          defaultClientName={
            userToEdit?.client_name || defaultClient?.client_name
          }
        />
      )}

      <FormField
        label="SourceWhale API Key"
        errors={
          errors.sourcewhale_key?.message
            ? [errors.sourcewhale_key.message]
            : undefined
        }
      >
        <Input
          isInvalid={!!errors.sourcewhale_key}
          defaultValue={savedSourcewhaleKey || ''}
          {...useFormMethods.register('sourcewhale_key', {
            pattern: {
              value: /^[A-Za-z0-9_-]+$/,
              message: 'Invalid SourceWhale API key format',
            },
          })}
        />
      </FormField>

      {isRevelioAdmin && (
        <FormField label="Skip Share of Postings V2 Scaling">
          <Switch
            id="skip_share_of_postings_scaling"
            size="sm"
            inactiveLabel="Off"
            label="On"
            disabled={!isRevelioAdmin}
            defaultChecked={
              localStorage.getItem(StorageKey.SkipShareOfPostingsScaling) ===
              'true'
            }
            {...useFormMethods.register('skip_share_of_postings_scaling')}
          />
        </FormField>
      )}
    </div>
  );
};

type SubmitEditUserArgs = {
  updateUser: (
    variables: UpdateUserMutationVariables,
    context?: Partial<OperationContext> | undefined
  ) => Promise<OperationResult<UpdateUserMutation, AnyVariables>>;
  userId: string;
  toast: (options: UseToastOptions) => void;
  onSuccess?: () => void;
  currentUser: User;
  loggedInUser: User;
};
export const submitEditUser =
  ({
    updateUser,
    userId,
    toast,
    onSuccess,
    currentUser,
    loggedInUser,
  }: SubmitEditUserArgs) =>
  (formData: UserFormValues) => {
    const formatData: Omit<UpdateUserMutationVariables, 'id'> = {
      active: formData.active,
      name: formData.name,
      email: formData.email.trim(),
    };

    const metadataUpdates = [];

    if (formData.trialExpirationDate) {
      metadataUpdates.push({
        key: MetadataKey.ExpirationDate,
        value: formData.trialExpirationDate,
      });
    }

    if (
      formData.sourcewhale_key ||
      getMetadataValue(currentUser.metadata, MetadataKey.SourcewhaleKey)
    ) {
      metadataUpdates.push({
        key: MetadataKey.SourcewhaleKey,
        value: formData.sourcewhale_key || '',
      });
    }

    if (metadataUpdates.length > 0) {
      if (currentUser.metadata && currentUser.metadata.length) {
        const updatedKeys = new Set(metadataUpdates.map((item) => item.key));
        const unchangedUserMetadata = currentUser.metadata.filter(
          (item) => item?.key && !updatedKeys.has(item.key)
        );
        formatData.metadata = [...unchangedUserMetadata, ...metadataUpdates];
      } else {
        formatData.metadata = metadataUpdates;
      }

      // Only Revelio admins can update the trial expiration date
      if (!isRoleRevelioAdmin(loggedInUser.role)) {
        formatData.metadata = formatData.metadata.filter(
          (item) => item?.key !== MetadataKey.ExpirationDate
        );
      }
    }

    if (formData.client_group) {
      formatData.client_name = formData.client_group.value;
    }

    if (formData.role) {
      formatData.role = formData.role.value;
    }

    if (formData.password && formData.password.length !== 0) {
      formatData.password = formData.password;
    }

    return updateUser({
      id: userId,
      ...formatData,
    }).then((result) => {
      if (result.error) {
        if (result.error.message.includes('seat limit')) {
          toast({
            title: 'User not added',
            description:
              'The seat limit has been reached for this client group.',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'top-right',
          });
        } else {
          return;
        }
      } else {
        toast({
          title: 'Account updated',
          description: 'The account has now been updated',
          status: 'success',
          duration: 4000,
          position: 'top-right',
        });

        onSuccess && onSuccess();
      }
    });
  };

export default UserForm;
